import { BaseModel, Paging } from '@Core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Badge,
    Box,
    CircularProgress,
    Collapse,
    Grow,
    IconButton,
    Pagination,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Tooltip,
} from '@mui/material';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { ReactNode, useEffect, useState } from 'react';
import { BaseHead } from './tables';
import color from '@theme/Colors';

type ExpandableRowProps<T extends BaseModel> = {
    item: T;
    index: number;
    isExpanded: boolean;
    onToggle: () => void;
    renderExpandedContent: (item: T) => ReactNode;
    children: ReactNode;
    vehicleCount?: number;
    vehiclePlates?: string[];
};

function ExpandableRow<T extends BaseModel>({
    item,
    index,
    isExpanded,
    onToggle,
    renderExpandedContent,
    children,
    vehicleCount = 0,
    vehiclePlates = [],
}: ExpandableRowProps<T>) {
    return (
        <>
            <Grow in={true} key={index} timeout={Math.min(500 * index, 1000)}>
                <TableRow
                    sx={{
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'grey.50',
                        },
                    }}
                    onClick={onToggle}
                >
                    <TableCell sx={{ width: '20px', padding: '0px' }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={vehiclePlates.join(', ')} placement="top" arrow>
                            <Badge
                                badgeContent={vehicleCount}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        fontSize: '10px',
                                        height: '16px',
                                        minWidth: '16px',
                                        padding: '0px',
                                        top: '4px',
                                        right: '4px',
                                        bgcolor: color.grey400,
                                    },
                                }}
                            >
                                <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={onToggle}
                                    sx={{ padding: 0.5 }}
                                >
                                    {isExpanded ? (
                                        <KeyboardArrowUp fontSize="small" />
                                    ) : (
                                        <KeyboardArrowDown fontSize="small" />
                                    )}
                                </IconButton>
                            </Badge>
                        </Tooltip>
                    </TableCell>
                    {children}
                </TableRow>
            </Grow>
            {isExpanded && (
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
                        <Collapse in={isExpanded} timeout={300} unmountOnExit>
                            <Box sx={{ margin: 2 }}>{renderExpandedContent(item)}</Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

type Props<T extends BaseModel> = {
    paging?: Paging<T>;
    styleContainer?: SxProps<Theme>;
    isLoading?: boolean;
    setPage?: (page: number) => void;
    renderedItem?: (item: T, index?: number) => JSX.Element;
    renderExpandedContent?: (item: T) => ReactNode;
    noDataComponent?: ReactNode;
    tableHeaders?: string[];
    isCheckedScroll?: boolean;
    isVerificationPage?: boolean;
    expandable?: boolean;
};

export const tableHeaders: string[] = ['Created date', 'Created by', 'Last update', 'Note'];

export default function BaseTable<T extends BaseModel>(props: Props<T>) {
    const {
        paging,
        setPage,
        isLoading,
        styleContainer,
        renderedItem,
        renderExpandedContent,
        noDataComponent,
        tableHeaders,
        isCheckedScroll,
        isVerificationPage,
        expandable = false,
    } = props;

    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        if (paging?.rows?.length && expandable && renderExpandedContent && !hasInitialized) {
            setExpandedRows(new Set([0]));
            setHasInitialized(true);
        }
    }, [paging?.rows, expandable, renderExpandedContent, hasInitialized]);

    const handleToggleExpand = (index: number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedRows(newExpanded);
    };

    return (
        <Stack sx={{ ...styleContainer }}>
            {isLoading ? (
                <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={isVerificationPage ? 100 : 300}
                >
                    <CircularProgress size={isVerificationPage ? 30 : 40} />
                </Stack>
            ) : (
                <>
                    {paging?.rows?.length ? (
                        <TableContainer
                            component={Box}
                            sx={
                                isCheckedScroll
                                    ? {
                                          '& th': {},
                                          maxHeight: 500,
                                          overflowY: 'auto',
                                          overflowX: 'auto',
                                          '::-webkit-scrollbar': {
                                              width: '3px',
                                          },
                                      }
                                    : {}
                            }
                        >
                            <Table aria-label="simple table">
                                <TableHead
                                    sx={
                                        isCheckedScroll
                                            ? {
                                                  position: 'sticky',
                                                  top: 0,
                                                  zIndex: 5,
                                              }
                                            : {}
                                    }
                                >
                                    <TableRow>
                                        {expandable && <BaseHead align="left" title="" />}
                                        {tableHeaders?.map((t, i) => {
                                            return <BaseHead key={i} align="left" title={t} />;
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paging?.rows?.map((item, index) => {
                                        const isExpanded = expandedRows.has(index);

                                        if (expandable && renderExpandedContent) {
                                            const vehicleCount = (item as any).VehicleExempts?.length || 0;
                                            const vehiclePlates =
                                                (item as any).VehicleExempts?.map((v: any) => v.Plate) || [];
                                            return (
                                                <ExpandableRow
                                                    key={index}
                                                    item={item}
                                                    index={index}
                                                    isExpanded={isExpanded}
                                                    onToggle={() => handleToggleExpand(index)}
                                                    renderExpandedContent={renderExpandedContent}
                                                    vehicleCount={vehicleCount}
                                                    vehiclePlates={vehiclePlates}
                                                >
                                                    {renderedItem?.(item, index)}
                                                </ExpandableRow>
                                            );
                                        }

                                        return (
                                            <Grow in={true} key={index} timeout={Math.min(500 * index, 1000)}>
                                                <TableRow
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: 'grey.50',
                                                        },
                                                    }}
                                                >
                                                    {renderedItem?.(item, index)}
                                                </TableRow>
                                            </Grow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        (noDataComponent ?? (
                            <ListNoData
                                sx={{
                                    border: isVerificationPage ? 'none' : '1px solid #DDDDDD',
                                    minHeight: isVerificationPage ? 200 : 150,
                                }}
                            />
                        ))
                    )}

                    {paging?.total! > paging?.pageSize! && !isLoading && (
                        <Stack mt={3} direction="row" justifyContent="center">
                            <Pagination
                                count={paging?.totalPages || 1}
                                page={paging?.page}
                                onChange={(_, page) => setPage?.(page)}
                                variant="outlined"
                                shape="rounded"
                            />
                        </Stack>
                    )}
                </>
            )}
        </Stack>
    );
}
